import React from 'react';
import { useNavigate } from 'react-router-dom';

interface TestSuite {
  id: string;
  name: string;
  description: string;
  route: string;
  category: 'Database' | 'API' | 'Feature' | 'UI' | 'Integration';
  status?: 'implemented' | 'planned' | 'in-progress';
  icon: string;
}

const testSuites: TestSuite[] = [
  {
    id: 'd1-test',
    name: 'D1 Database Tests',
    description: 'Test all CRUD operations against production D1',
    route: '/d1-test',
    category: 'Database',
    status: 'implemented',
    icon: '🗄️'
  },
  {
    id: 'resolution-test',
    name: 'Market Resolution',
    description: 'Test market resolution workflow end-to-end',
    route: '/test/resolution',
    category: 'Feature',
    status: 'implemented',
    icon: '🏁'
  },
  {
    id: 'auth-test',
    name: 'Authentication Flow',
    description: 'Test user registration, login, and session management',
    route: '/test/auth',
    category: 'Feature',
    status: 'planned',
    icon: '🔐'
  },
  {
    id: 'betting-test',
    name: 'Betting Engine',
    description: 'Test bet placement, validation, and probability updates',
    route: '/test/betting',
    category: 'Feature',
    status: 'implemented',
    icon: '💰'
  },
  {
    id: 'betting-visual',
    name: 'Betting Flow Visual',
    description: 'Visual step-by-step betting flow demonstration',
    route: '/betting-flow',
    category: 'Feature',
    status: 'implemented',
    icon: '🎯'
  },
  {
    id: 'api-test',
    name: 'API Endpoints',
    description: 'Test all API endpoints for correct responses',
    route: '/test/api',
    category: 'API',
    status: 'planned',
    icon: '🔌'
  },
  {
    id: 'ui-test',
    name: 'UI Components',
    description: 'Visual regression and interaction tests',
    route: '/test/ui',
    category: 'UI',
    status: 'planned',
    icon: '🎨'
  },
  {
    id: 'performance-test',
    name: 'Performance Metrics',
    description: 'Test load times, response times, and optimization',
    route: '/test/performance',
    category: 'Integration',
    status: 'planned',
    icon: '⚡'
  },
  {
    id: 'search-test',
    name: 'Search & Discovery',
    description: 'Test market search, filtering, and recommendations',
    route: '/test/search',
    category: 'Feature',
    status: 'planned',
    icon: '🔍'
  },
  {
    id: 'notifications-test',
    name: 'Notifications',
    description: 'Test email and in-app notification delivery',
    route: '/test/notifications',
    category: 'Integration',
    status: 'planned',
    icon: '🔔'
  },
  {
    id: 'leaderboard-test',
    name: 'Leaderboard Logic',
    description: 'Test ranking calculations and updates',
    route: '/test/leaderboard',
    category: 'Feature',
    status: 'planned',
    icon: '🏆'
  },
  {
    id: 'agent-builder',
    name: 'Claude Agent Builder',
    description: 'Interactive demo of autonomous project building',
    route: '/agent-builder',
    category: 'Feature',
    status: 'implemented',
    icon: '🤖'
  }
];

export default function TestDashboard() {
  const navigate = useNavigate();

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Database': return 'from-blue-500 to-blue-600';
      case 'API': return 'from-purple-500 to-purple-600';
      case 'Feature': return 'from-green-500 to-green-600';
      case 'UI': return 'from-pink-500 to-pink-600';
      case 'Integration': return 'from-orange-500 to-orange-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'implemented':
        return <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">Ready</span>;
      case 'in-progress':
        return <span className="px-2 py-1 bg-yellow-500 text-white text-xs rounded-full">WIP</span>;
      case 'planned':
        return <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">Planned</span>;
      default:
        return null;
    }
  };

  const categories = Array.from(new Set(testSuites.map(t => t.category)));

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">🧪 Test Dashboard</h1>
              <p className="text-gray-400">Comprehensive testing for every feature before deployment</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              ← Back to App
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-3xl font-bold text-green-500">
                {testSuites.filter(t => t.status === 'implemented').length}
              </div>
              <div className="text-sm text-gray-400">Implemented</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-3xl font-bold text-yellow-500">
                {testSuites.filter(t => t.status === 'in-progress').length}
              </div>
              <div className="text-sm text-gray-400">In Progress</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-3xl font-bold text-gray-500">
                {testSuites.filter(t => t.status === 'planned').length}
              </div>
              <div className="text-sm text-gray-400">Planned</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-500">
                {testSuites.length}
              </div>
              <div className="text-sm text-gray-400">Total Tests</div>
            </div>
          </div>
        </div>

        {/* Test Suites by Category */}
        {categories.map(category => (
          <div key={category} className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <span className={`px-3 py-1 rounded-lg bg-gradient-to-r ${getCategoryColor(category)}`}>
                {category}
              </span>
              <span className="text-gray-400 text-sm">
                ({testSuites.filter(t => t.category === category).length} tests)
              </span>
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testSuites
                .filter(t => t.category === category)
                .map(test => (
                  <div
                    key={test.id}
                    onClick={() => test.status === 'implemented' && navigate(test.route)}
                    className={`bg-gray-800 rounded-lg p-6 border border-gray-700 transition-all ${
                      test.status === 'implemented'
                        ? 'hover:border-green-500 hover:shadow-lg cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-3xl">{test.icon}</span>
                      {getStatusBadge(test.status)}
                    </div>
                    <h3 className="font-bold text-lg mb-2">{test.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{test.description}</p>
                    {test.status === 'implemented' && (
                      <div className="text-green-500 text-sm font-semibold">
                        → Run Tests
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}

        {/* Quick Actions */}
        <div className="mt-12 p-6 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="font-bold text-lg mb-4">🚀 Quick Actions</h3>
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => {
                // Run all implemented tests
                const implemented = testSuites.filter(t => t.status === 'implemented');
                alert(`Would run ${implemented.length} test suites`);
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            >
              Run All Tests
            </button>
            <button
              onClick={() => {
                // Generate test report
                alert('Test report generation coming soon');
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Generate Report
            </button>
            <button
              onClick={() => {
                // Export test results
                alert('Test export coming soon');
              }}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
            >
              Export Results
            </button>
          </div>
        </div>

        {/* Development Process Link */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Following the <a href="/DEVELOPMENT_PROCESS.md" className="text-blue-400 hover:underline">Test-Driven Development Process</a></p>
          <p className="mt-2">No feature without tests • No deployment without green checks</p>
        </div>
      </div>
    </div>
  );
}